import { useAuthContext } from '@/auth/useAuthContext';
import Iconify from '@/components/iconify';
import { useErrorSnackbar } from '@/components/snackbar/useErrorSnackbar';
import { AffiliateStatus } from '@/contexts/AffiliatesContext';
import { createAffiliateService } from '@/services/affiliates';
import { getProductCategoriesService } from '@/services/products';
import { fCurrency, formatPercentage } from '@/utils/formatNumber';
import { LoadingButton } from '@mui/lab';
import {
  Box,
  CardMedia,
  Divider,
  Drawer,
  IconButton,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { Stack } from '@mui/system';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import PropTypes from 'prop-types';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Label from '../../../components/label';

export default function ProductDetailsDrawer({ open, onClose, onAffiliate, product }) {
  const { user } = useAuthContext();
  const { enqueueSnackbar } = useSnackbar();
  const { appErrorRenderer } = useErrorSnackbar();
  const [askedAffiliation, setAskedAffiliation] = useState(false);
  const navigate = useNavigate();

  const productCategoriesQuery = useQuery({
    queryKey: ['product-categories'],
    queryFn: () => getProductCategoriesService(),
    enabled: open,
  });

  const getCategoryName = (categoryId) => {
    if (!productCategoriesQuery.data || !categoryId) return '-';

    const category = productCategoriesQuery.data.find((cat) => cat.id === categoryId);
    return category ? category.name : '-';
  };

  const getProductType = (type) => {
    switch (type) {
      case 'unique':
        return 'Pagamento único';
      case 'subscription':
        return 'Assinatura';
      default:
        return '-';
    }
  };

  const getAffiliateClick = (affiliateClick) => {
    switch (affiliateClick) {
      case 'first':
        return 'Primeiro clique';
      case 'last':
        return 'Último clique';
      default:
        return '-';
    }
  };

  const createAffiliateMutation = useMutation({
    mutationFn: createAffiliateService,
    onSuccess: async ({ short_id }, { productPk }) => {
      await onAffiliate();
      enqueueSnackbar('Afiliação criada com sucesso!', { variant: 'success' });
      setAskedAffiliation(true);
      navigate(`/dashboard/products/${productPk}/affiliates/${short_id}?tab=links`);
    },
    onError: (error) => {
      appErrorRenderer({ error });
    },
  });

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          p: 2,
          width: {
            xs: '100%',
            sm: 400,
          },
          bgcolor: 'background.default',
          color: 'white',
        },
      }}
    >
      <Stack gap={2}>
        <Stack direction="row" alignItems="start" justifyContent="space-between">
          <Stack>
            <CardMedia
              component="img"
              height={112}
              width={149}
              image={product?.image}
              alt="Course Image"
              sx={{
                borderRadius: 3,
                height: 112,
                width: 149,
                objectFit: 'cover',
              }}
            />
          </Stack>
          <IconButton
            product-testid="close-drawer"
            onClick={onClose}
            sx={{ color: 'text.primary' }}
          >
            <Iconify icon="eva:close-fill" />
          </IconButton>
        </Stack>

        <Box display="flex" justifyContent="space-between">
          <Box color="text.primary">
            <Typography variant="h5">{product?.name}</Typography>
            <Typography variant="body2" color="text.secondary">
              Por {product?.author}
            </Typography>
          </Box>

          {product?.affiliation && (
            <Box>
              <Tooltip title="Status da sua afiliação" arrow placement="top">
                <Label
                  variant="soft"
                  color={AffiliateStatus[product.affiliation.status]?.color}
                  sx={{
                    fontSize: '0.875rem',
                    padding: '4px 8px',
                  }}
                >
                  {AffiliateStatus[product.affiliation.status].label}
                </Label>
              </Tooltip>
            </Box>
          )}
        </Box>

        <Divider />

        {!askedAffiliation && product?.affiliation === null && (
          <>
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle1" color="primary" gutterBottom>
                AFILIAÇÃO
              </Typography>
              <LoadingButton
                variant="contained"
                onClick={() =>
                  createAffiliateMutation.mutate({
                    userEmail: user.email,
                    productPk: product.id,
                    isFromShowcase: true,
                  })
                }
                loading={createAffiliateMutation.isLoading}
                color="primary"
                sx={{
                  width: 1,
                }}
              >
                Solicitar afiliação
              </LoadingButton>
            </Box>

            <Divider />
          </>
        )}

        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle1" color="primary" gutterBottom>
            PRODUTO
          </Typography>

          <Stack gap={2}>
            <Stack direction="row" justifyContent="space-between">
              <Typography color="text.secondary">Categoria</Typography>
              <Typography color="text.primary">{getCategoryName(product?.category)}</Typography>
            </Stack>

            <Stack direction="row" justifyContent="space-between">
              <Typography color="text.secondary">Tipo</Typography>
              <Typography color="text.primary">{getProductType(product?.type)}</Typography>
            </Stack>

            <Stack direction="row" justifyContent="space-between">
              <Typography color="text.secondary">Receba até</Typography>
              <Typography color="primary">
                {fCurrency(product?.affiliate_commission_amount)}
              </Typography>
            </Stack>

            <Stack direction="row" justifyContent="space-between">
              <Typography color="text.secondary">Porcentagem de comissão</Typography>
              <Typography color="primary">
                {formatPercentage(product?.affiliate_commission)}
              </Typography>
            </Stack>

            <Box color="text.primary">
              <Typography color="text.secondary" gutterBottom>
                Descrição
              </Typography>
              <Typography>{product?.description}</Typography>
            </Box>
          </Stack>
        </Box>

        <Divider />

        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle1" color="primary" gutterBottom>
            DETALHES
          </Typography>

          <Stack gap={2}>
            <Stack direction="row" justifyContent="space-between">
              <Typography color="text.secondary">Atribuição</Typography>
              <Typography color="text.primary">
                {getAffiliateClick(product?.affiliate_click)}
              </Typography>
            </Stack>

            <Stack direction="row" justifyContent="space-between">
              <Typography color="text.secondary">Duração dos cookies</Typography>
              <Typography color="text.primary">
                {product?.cookie_duration === -1 ? 'Infinito' : product?.cookie_duration}
              </Typography>
            </Stack>

            <Stack direction="row" justifyContent="space-between">
              <Typography color="text.secondary">Solicitação do afiliado</Typography>
              <Typography color="text.primary">
                {product?.affiliate_request ? 'Sim' : 'Não'}
              </Typography>
            </Stack>

            <Stack direction="row" justifyContent="space-between">
              <Typography color="text.secondary">Contato do afiliado</Typography>
              <Typography color="text.primary">
                {product?.affiliate_contact ? 'Sim' : 'Não'}
              </Typography>
            </Stack>

            <Stack direction="row" justifyContent="space-between">
              <Typography color="text.secondary">Compartilhar bump</Typography>
              <Typography color="text.primary">
                {product?.affiliate_share_bump ? 'Sim' : 'Não'}
              </Typography>
            </Stack>

            <Stack direction="row" justifyContent="space-between">
              <Typography color="text.secondary">Compartilhar upsell</Typography>
              <Typography color="text.primary">
                {product?.affiliate_share_upsell ? 'Sim' : 'Não'}
              </Typography>
            </Stack>

            {!!product?.affiliate_sales_page && (
              <Stack direction="row" justifyContent="space-between">
                <Typography
                  color="text.secondary"
                  sx={{
                    maxWidth: 150,
                    lineHeight: 1.2,
                    whiteSpace: 'normal',
                    wordBreak: 'break-word',
                  }}
                >
                  URL da página do afiliado
                </Typography>
                <Link
                  href={product?.affiliate_sales_page}
                  target="_blank"
                  sx={{
                    color: 'primary',
                    maxWidth: 180,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    display: 'block',
                  }}
                >
                  {product?.affiliate_sales_page}
                </Link>
              </Stack>
            )}

            <Stack direction="row" justifyContent="space-between">
              <Typography
                color="text.secondary"
                sx={{
                  maxWidth: 150,
                  lineHeight: 1.2,
                  whiteSpace: 'normal',
                  wordBreak: 'break-word',
                }}
              >
                URL da página de vendas
              </Typography>
              <Link
                href={product?.sales_page}
                target="_blank"
                sx={{
                  color: 'primary',
                  maxWidth: 180,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  display: 'block',
                }}
              >
                {product?.sales_page}
              </Link>
            </Stack>
          </Stack>
        </Box>

        <Divider />

        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle1" color="primary" gutterBottom>
            OFERTAS
          </Typography>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Nome</TableCell>
                  <TableCell>Preço</TableCell>
                  <TableCell>Você recebe</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell sx={{ display: 'flex' }}>{product?.name}</TableCell>
                  <TableCell>R$ {product?.price}</TableCell>
                  <TableCell>{fCurrency(product?.affiliate_commission_amount)}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Stack>
    </Drawer>
  );
}

ProductDetailsDrawer.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onAffiliate: PropTypes.func,
  product: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    author: PropTypes.string,
    image: PropTypes.string,
    affiliate_commission: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    price: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    sales_page: PropTypes.string,
    description: PropTypes.string,
    affiliate_support_email: PropTypes.string,
    status: PropTypes.string,
    category: PropTypes.string,
    type: PropTypes.string,
    cookie_duration: PropTypes.number,
    affiliate_click: PropTypes.string,
    affiliation: PropTypes.object,
    affiliate_commission_amount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    affiliate_request: PropTypes.bool,
    affiliate_contact: PropTypes.bool,
    affiliate_share_upsell: PropTypes.bool,
    affiliate_share_bump: PropTypes.bool,
    affiliate_sales_page: PropTypes.string,
  }),
};
